import React, { useState, useEffect } from 'react';
import { ModalHeader, ModalBody, ModalFooter, Col } from 'reactstrap';
import { Form, Formik, Field, ErrorMessage } from 'formik';
import * as Yup from 'yup';
import { toast } from 'react-toastify';
import TextError from '../../helpers/FormikHelper/TextError';
import configServices from '../../services/config.services';

const ConfigurationForm = ({ toggle, category, categoryLabel, configFields, getData }) => {
    const [initialLoading, setInitialLoading] = useState(true);
    const [initialValues, setInitialValues] = useState({});

    // Load existing configurations on mount
    useEffect(() => {
        loadConfigurations();
    }, [category]);

    const loadConfigurations = async () => {
        setInitialLoading(true);
        try {
            const response = await configServices.getConfigByCategory(category);
            if (response.data.status === true) {
                const loadedData = response.data.data || {};
                // Set initial values for Formik
                const formValues = {};
                configFields.forEach(field => {
                    formValues[field.key] = loadedData[field.key] || '';
                });
                setInitialValues(formValues);
            }
        } catch (error) {
            console.error('Error loading configurations:', error);
            toast.error('Failed to load configuration', { autoClose: 3000 });
        } finally {
            setInitialLoading(false);
        }
    };

    // Build Yup validation schema dynamically
    const buildValidationSchema = () => {
        const schemaFields = {};

        configFields.forEach(field => {
            let fieldSchema;

            switch (field.type) {
                case 'time':
                    fieldSchema = Yup.string()
                        .matches(/^([0-1]?[0-9]|2[0-3]):[0-5][0-9]$/, 'Invalid time format (HH:mm)');
                    break;

                case 'number':
                    fieldSchema = Yup.number()
                        .typeError('Must be a valid number');
                    if (field.min !== undefined) {
                        fieldSchema = fieldSchema.min(field.min, `Must be at least ${field.min}`);
                    }
                    if (field.max !== undefined) {
                        fieldSchema = fieldSchema.max(field.max, `Must be at most ${field.max}`);
                    }
                    break;

                case 'email':
                    fieldSchema = Yup.string()
                        .email('Invalid email format');
                    break;

                default:
                    fieldSchema = Yup.string();
            }

            if (field.required) {
                fieldSchema = fieldSchema.required(`${field.label} is required`);
            }

            // Add custom validation if provided
            if (field.validate) {
                fieldSchema = fieldSchema.test(
                    'custom-validation',
                    'Validation failed',
                    function (value) {
                        const error = field.validate(value, this.parent);
                        if (error) {
                            return this.createError({ message: error });
                        }
                        return true;
                    }
                );
            }

            schemaFields[field.key] = fieldSchema;
        });

        return Yup.object().shape(schemaFields);
    };

    const onSubmit = async (values, actions) => {
        try {
            // Save each configuration field
            const savePromises = configFields.map(field => {
                const value = values[field.key];
                if (value !== undefined && value !== null && value !== '') {
                    return configServices.saveConfig({
                        config_key: field.key,
                        config_value: value,
                        config_category: category
                    });
                }
                return Promise.resolve();
            });

            const results = await Promise.all(savePromises);

            // Check if all saves were successful
            const allSuccess = results.every(result =>
                !result || result.data?.status === true
            );

            if (allSuccess) {
                toast.success('Configuration saved successfully', {
                    toastId: 'config_save_success',
                    autoClose: 3000
                });
                if (getData) getData();
                toggle();
            } else {
                toast.error('Some configurations failed to save', {
                    toastId: 'config_save_fail',
                    autoClose: 3000
                });
            }
        } catch (error) {
            console.error('Error saving configuration:', error);
            toast.error('Failed to save configuration', {
                toastId: 'config_save_error',
                autoClose: 3000
            });
        }
    };

    const renderField = (field, setFieldValue, values) => {
        const value = values[field.key] || '';

        switch (field.type) {
            case 'time':
                return (
                    <>
                        <Field
                            type="time"
                            className="form-control"
                            id={field.key}
                            name={field.key}
                            placeholder={field.placeholder}
                        />
                        <ErrorMessage name={field.key} component={TextError} />
                    </>
                );

            case 'number':
                return (
                    <>
                        <Field
                            type="number"
                            className="form-control"
                            id={field.key}
                            name={field.key}
                            placeholder={field.placeholder}
                            min={field.min}
                            max={field.max}
                            step={field.step || 1}
                        />
                        <ErrorMessage name={field.key} component={TextError} />
                    </>
                );

            case 'textarea':
                return (
                    <>
                        <textarea
                            className="form-control"
                            id={field.key}
                            name={field.key}
                            value={value}
                            onChange={(e) => setFieldValue(field.key, e.target.value)}
                            placeholder={field.placeholder}
                            rows={field.rows || 3}
                        />
                        <ErrorMessage name={field.key} component={TextError} />
                    </>
                );

            case 'select':
                return (
                    <>
                        <Field
                            as="select"
                            className="form-control"
                            id={field.key}
                            name={field.key}
                        >
                            <option value="">Select {field.label}</option>
                            {field.options?.map(option => (
                                <option key={option.value} value={option.value}>
                                    {option.label}
                                </option>
                            ))}
                        </Field>
                        <ErrorMessage name={field.key} component={TextError} />
                    </>
                );

            case 'email':
                return (
                    <>
                        <Field
                            type="email"
                            className="form-control"
                            id={field.key}
                            name={field.key}
                            placeholder={field.placeholder}
                        />
                        <ErrorMessage name={field.key} component={TextError} />
                    </>
                );

            default: // text
                return (
                    <>
                        <Field
                            type="text"
                            className="form-control"
                            id={field.key}
                            name={field.key}
                            placeholder={field.placeholder}
                        />
                        <ErrorMessage name={field.key} component={TextError} />
                    </>
                );
        }
    };

    if (initialLoading) {
        return (
            <React.Fragment>
                <ModalHeader toggle={toggle}>
                    <div className="card-title d-flex align-items-center">
                        <div>
                            <i className="bx bx-cog me-1 font-22 text-primary" />
                        </div>
                        <h5 className="mb-0 text-primary">{categoryLabel} Configuration</h5>
                    </div>
                </ModalHeader>
                <ModalBody>
                    <div className="text-center py-5">
                        <div className="spinner-border text-primary" role="status">
                            <span className="visually-hidden">Loading...</span>
                        </div>
                    </div>
                </ModalBody>
            </React.Fragment>
        );
    }

    return (
        <React.Fragment>
            <Formik
                initialValues={initialValues}
                validationSchema={buildValidationSchema()}
                onSubmit={onSubmit}
                enableReinitialize
            >
                {({ setFieldValue, values, isSubmitting }) => (
                    <Form>
                        <ModalHeader toggle={toggle}>
                            <div className="card-title d-flex align-items-center">
                                <div>
                                    <i className="bx bx-cog me-1 font-22 text-primary" />
                                </div>
                                <h5 className="mb-0 text-primary">{categoryLabel} Configuration</h5>
                            </div>
                        </ModalHeader>
                        <ModalBody>
                            <Col md={12}>
                                <div className="row g-3">
                                    {configFields.map(field => (
                                        <div className="col-md-12" key={field.key}>
                                            <label htmlFor={field.key} className="form-label">
                                                {field.label}
                                                {field.required && <span className="text-danger">*</span>}
                                            </label>
                                            {renderField(field, setFieldValue, values)}
                                            {field.helpText && (
                                                <small className="form-text text-muted">
                                                    {field.helpText}
                                                </small>
                                            )}
                                        </div>
                                    ))}
                                </div>
                            </Col>
                        </ModalBody>
                        <ModalFooter>
                            <button
                                type="button"
                                className="btn btn-secondary"
                                onClick={toggle}
                                disabled={isSubmitting}
                            >
                                Close
                            </button>
                            <button
                                type="submit"
                                className="btn btn-primary px-5"
                                disabled={isSubmitting}
                            >
                                {isSubmitting ? 'Saving...' : 'Save'}
                            </button>
                        </ModalFooter>
                    </Form>
                )}
            </Formik>
        </React.Fragment>
    );
};

export default ConfigurationForm;

